/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import {
  BuilderUserContext,
  CommunicationEntryPoint,
  CommunicationListItem,
  EmailDialogConfig,
  emailDialogRoute,
  MeetingDialogConfig,
  meetingDialogRoute,
  SmsDialogConfig,
  smsDialogRoute,
  WebsiteDialogConfig,
  websiteDialogRoute,
} from '@gh/builder-shared';
import {
  BuilderPermission,
  BuilderPermissionSet,
  CommunicationType,
  ContactType,
  SystemRoleBit,
} from '@gh/builder-shared-data';
import {
  ContextRoute,
  getContextRouteInitData,
  getContextRouteStaticData,
  textMessageDialogRoute,
} from '@gh/common-internal-shared';
import { TranslationService } from '@gh/core-mls';
import {
  DialogService,
  FormMode,
  getControlValue,
  NotificationService,
  setControlValue,
  TextMessageDialogConfig,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { GridRowEvent } from '@gh/core-ui-grid';
import { filter$ } from '@gh/rx/operators';
import { assign, concat, filter, first } from 'lodash';
import { ContactDetailsModel } from '../../services/contact-details-model.service';
import { ContactDetailsFormState } from '../contact-details-tab-container/contact-details-form.service';
import {
  ContactDetailsSharedCommunicationGridDefinition,
} from './contact-details-shared-communication-grid-definition.service';
import {
  ContactDetailsSharedCommunicationListFormState,
} from './contact-details-shared-communication-list-form.service';
import { ContactDetailsBusinessService } from '../../resources/contact-details-business.service';
import { createLink } from '@gh/core-util';
import { Permission } from '@gh/core-auth';

@Component({
  standalone: false,
  selector: 'gh-contact-details-shared-communication-tab',
  templateUrl: 'contact-details-shared-communication-list-tab.component.html',
  styleUrls: ['contact-details-shared-communication-list-tab.scss'],
  providers: [ContactDetailsSharedCommunicationGridDefinition],
})
export class ContactDetailsSharedCommunicationListTabComponent implements OnInit {
  form: ContactDetailsSharedCommunicationListFormState;
  showSms: boolean = true;
  showEmail: boolean = true;
  showWebsite: boolean = true;
  showDocs: boolean = true;
  showActivities: boolean = true;
  contactType: any;
  filteredCommunications: CommunicationListItem[];
  isUnsubscribed: boolean;
  hasPermission: boolean;
  get isCreateMode(): boolean {
    return this.model.creationMode;
  }

  get editable(): boolean {
    return this.form.group.enabled;
  }

  get hasRole(): boolean {
  return !!this.contactType && 
         this.userContext.hasPermission(BuilderPermission.Contacts[this.contactType as ContactType].CREATE_EDIT);
  }

  get hasPermissionViewCommunicationDetails(): boolean {
    return this.userContext.hasPermission(this.getContactOpenPermission(this.contactType));
  }

  constructor(private route: ContextRoute,
              private translations: TranslationService,
              private rootForm: ContactDetailsFormState,
              private contactBusinessService: ContactDetailsBusinessService,
              public gridDefinition: ContactDetailsSharedCommunicationGridDefinition,
              private waitingService: WaitingService,
              private notificationService: NotificationService,
              private dialogService: DialogService,
              private model: ContactDetailsModel,
              private userContext: BuilderUserContext) {
    this.form = <ContactDetailsSharedCommunicationListFormState>rootForm
      .getChildForm(getContextRouteStaticData(this.route)['tab'])
      .getChildForm('communication');
  }

  ngOnInit(): void {
    assign(this, getContextRouteInitData(this.route));
    this.contactType =  getContextRouteStaticData(this.route)['contactType'];
    this.setHasPermission();
    this.isUnsubscribed = this.model.original.common.isUnsubscribed;
    this.updateFilteredCommunications();
  }

  setHasPermission(): void {
    this.hasPermission =  this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasPermission(BuilderPermission.Contacts[this.contactType as ContactType].CREATE_EDIT);
  }

  updateFilteredCommunications(): void {
    const communications: CommunicationListItem[] = this.form.getControl('communications').value;
    if (!this.showEmail && !this.showSms && !this.showWebsite && !this.showActivities && !this.showDocs) {
      this.filteredCommunications = [];
    } else {
      this.filteredCommunications = filter(communications, ({ communicationType, subject }) => {
        if (this.showEmail
          && communicationType === CommunicationType.Email) {
          return true;
        } else if (this.showSms
          && (communicationType === CommunicationType.OutgoingSms
            || communicationType === CommunicationType.IncomingSms)) {
          return true;
        } else if (this.showWebsite && communicationType === CommunicationType.Website) {
          return true;
        } else if (this.showDocs && communicationType === CommunicationType.Doc) {
          return true;
        } else if (this.showActivities
          && (communicationType === CommunicationType.ContactActivity
          || communicationType === CommunicationType.SalesActivity
          || communicationType === CommunicationType.JobActivity)) {
          return true;
        }  else {
          return false;
        }
      });
    }
  }

  sendEmail(): void {
    const { rootForm, validEmails } = this.form;
    const contactId = rootForm.value.id;
    const contactTypeCode = getContextRouteStaticData(this.route)['contactType'];

    this.dialogService.openWithInput(emailDialogRoute, <EmailDialogConfig>{
      entryPoint: CommunicationEntryPoint.Contact,
      contactId,
      email: {
        email: validEmails,
        subject: '',
        message: '',
        communicationType: CommunicationType.Email,
        attachments: [],
        contactId,
        contactTypeCode,
        isMarkedAsRead: true,
      },
      gFolderId: this.form.gDriveFolderId,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((addresseeEmail) => {
        setControlValue(
          this.form.group,
          'communications',
          concat(addresseeEmail, getControlValue(this.form.group, 'communications')));
        this.updateFilteredCommunications();
      });
  }

  sendSms(): void {
    const { rootForm } = this.form;
    const cellPhone = rootForm.value.common?.phone1;
    const otherPhone = rootForm.value.common?.phone2;
    const contactId = rootForm.value.id;
    const contactTypeCode = getContextRouteStaticData(this.route)['contactType'];

    this.dialogService.openWithInput(smsDialogRoute, <SmsDialogConfig>{
        entryPoint: CommunicationEntryPoint.Contact,
        contactId,
        sms: {
          cellPhone,
          otherPhone,
          subject: '',
          message: '',
          communicationType: CommunicationType.Email,
          attachments: [],
          contactId,
          contactTypeCode,
          isMarkedAsRead: false,
        },
      })
      .afterClosed().pipe(
      filter$(Boolean))
      .subscribe((sms) => {
        setControlValue(
          this.form.group,
          'communications',
          concat(sms, getControlValue(this.form.group, 'communications')));
        this.updateFilteredCommunications();
      });
  }

  showEmailDialog(communicationListItem: CommunicationListItem): void {
    const contactId = this.rootForm.value.id;
    const contactTypeCode = getContextRouteStaticData(this.route)['contactType'];
    this.dialogService.openWithInput(emailDialogRoute, <EmailDialogConfig>{
        entryPoint: CommunicationEntryPoint.Contact,
        contactId,
        email: communicationListItem,
        communicationId: communicationListItem.communicationId,
        contactTypeCode,
        mode: this.editable ? FormMode.EditMode : FormMode.ViewMode,
    }).afterClosed().pipe(
        filter$(Boolean))
        .subscribe();
  }

  showSmsDialog(communicationListItem: CommunicationListItem): void {
    const contactId = this.rootForm.value.id;
    const contactTypeCode = getContextRouteStaticData(this.route)['contactType'];

    this.dialogService.openWithInput(smsDialogRoute, <SmsDialogConfig>{
      entryPoint: CommunicationEntryPoint.Contact,
      contactId,
      mode: this.editable ? FormMode.EditMode : FormMode.ViewMode,
      sms: {
        communicationId: communicationListItem.communicationId,
        cellPhone: communicationListItem.cellPhone,
        message: communicationListItem.message,
        isMarkedAsRead: communicationListItem.isMarkedAsRead,
        contactTypeCode,
      },
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((item: CommunicationListItem) => {
        const updatedArray = getControlValue(this.form.group, 'communications').map((el: any) => {
          if (el.communicationId === item.communicationId) {
            el = item;
          }
          return el;
        });
        setControlValue(
          this.form.group,
          'communications',
          updatedArray);
        this.updateFilteredCommunications();
    });
  }
  showDoc(communicationListItem: CommunicationListItem): void {
    this.contactBusinessService
      .getCommunicationDetails(communicationListItem.communicationId!).pipe(
                                                                            this.waitingService.operator())
      .subscribe(
        (fileInfo: CommunicationListItem) => {
          const attachment = first(fileInfo.attachments);
          const link = createLink(attachment!.webViewLink, attachment!.googleFileName);

          this.dialogService.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
            title: this.translations.get('lbl_generated_doc'),
            html: `${this.translations.get('lbl_link_to_generated_doc')}:\n${link}`,
            buttons: [
              { title: this.translations.get('lbl_ok'), value: 'ok', type: 'primary' },
            ],
          });
        },
        this.notificationService.processAll);
  }
  showWebsiteDialog(communicationListItem: CommunicationListItem): void {
    this.dialogService.openWithInput(websiteDialogRoute, <WebsiteDialogConfig>{
      entryPoint: CommunicationEntryPoint.Lead,
      website: communicationListItem,
    }).afterClosed().pipe();
  }

  showEditActivityDialog(
    communicationListItem: CommunicationListItem,
    formMode: FormMode | boolean,
    entryPoint: CommunicationEntryPoint): void {
    if (!formMode) {
      this.dialogService.error('msg_denied_no_permissions');
      return;
    }
    const isDone = communicationListItem.isDone;
    this.dialogService.openWithInput(meetingDialogRoute, <MeetingDialogConfig>{
      entryPoint,
      jobId: entryPoint === CommunicationEntryPoint.Job ? communicationListItem.jobId : undefined,
      salesFolderId: entryPoint === CommunicationEntryPoint.LeadCommunication ? communicationListItem.salesFolderId : undefined,
      leadId: entryPoint === CommunicationEntryPoint.LeadCommunication ? communicationListItem.contactId : undefined,
      meetingId: communicationListItem.communicationId,
      contactId: communicationListItem.contactId,
      mode: formMode,
    }).afterClosed().pipe(filter$(Boolean))
      .subscribe((activity: CommunicationListItem) => {
        const communications: CommunicationListItem[] = this.form.getControl('communications').value;
        const updatedList = communications.map((item) =>
          item.communicationId === activity.communicationId ? activity : item);
        setControlValue(
          this.form.group,
          'communications',
          updatedList);
        this.updateFilteredCommunications();
        if (activity.isDone && !isDone) {
          this.dialogService.confirmation(yesNoConfirmation('msg_activity_marked_as_done')).pipe(
            filter$(yesAnswer))
            .subscribe(() => this.addActivity());
        }
      });
  }

  addActivity(): void {
    this.dialogService.openWithInput(meetingDialogRoute, <MeetingDialogConfig>{
      entryPoint: CommunicationEntryPoint.Contact,
      contactId: this.rootForm.group.get('id')?.value,
      mode: FormMode.CreateMode,
      contactTypeCode: getContextRouteStaticData(this.route)['contactType'],
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((meeting: any) => {
        setControlValue(
          this.form.group,
          'communications',
          concat(meeting, getControlValue(this.form.group, 'communications')));
        this.updateFilteredCommunications();
        if (meeting.isDone) {
          this.dialogService.confirmation(yesNoConfirmation('msg_activity_marked_as_done')).pipe(
            filter$(yesAnswer))
            .subscribe(() => this.addActivity());
        }
      });
  }

  onShowSmsChanged(value: boolean): void {
    this.showSms = value;
    this.updateFilteredCommunications();
  }

  onShowEmailChanged(value: boolean): void {
    this.showEmail = value;
    this.updateFilteredCommunications();
  }

  onShowWebsiteChanged(value: boolean): void {
    this.showWebsite = value;
    this.updateFilteredCommunications();
  }

  onShowDocsChanged(value: boolean): void {
    this.showDocs = value;
    this.updateFilteredCommunications();
  }

  onShowActivitiesChanged(value: boolean): void {
    this.showActivities = value;
    this.updateFilteredCommunications();
  }

  onRowClicked(event: GridRowEvent<CommunicationListItem>): void {
    const item = event.data;
    const communicationType = item.communicationType;
    const simpleTypes = [CommunicationType.Email, CommunicationType.OutgoingSms, CommunicationType.IncomingSms, CommunicationType.Website];
    if (simpleTypes.find((type) => type === communicationType)) {
      if (!this.hasRole && !this.hasPermissionViewCommunicationDetails) {
        return;
      }
    }
    switch (item.communicationType) {
      case CommunicationType.Email:
        this.showEmailDialog(item);
        break;
      case CommunicationType.OutgoingSms:
      case CommunicationType.IncomingSms:
        this.showSmsDialog(item);
        break;
      case CommunicationType.Website:
        this.showWebsiteDialog(item);
        break;
      case CommunicationType.Doc:
        this.showDoc(item);
        break;
      case CommunicationType.ContactActivity:
        this.showEditActivityDialog(item, this.hasPermission ? FormMode.EditMode : FormMode.ViewMode, CommunicationEntryPoint.Contact);
        break;
      case CommunicationType.SalesActivity:
        this.showEditActivityDialog(item, this.formModeForSalesActivity(), CommunicationEntryPoint.LeadCommunication);
        break;
      case CommunicationType.JobActivity:
        this.showEditActivityDialog(item, this.formModeForJobActivity(), CommunicationEntryPoint.Job);
        break;
      default:
        break;
    }

  }
  private formModeForJobActivity(): FormMode | boolean {
    if (this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.EDIT)) {
      return FormMode.EditMode;
    }
    if (this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.VIEW)) {
      return  FormMode.ViewMode;
    }
    return false;
  }

  private getContactOpenPermission(type: ContactType): Permission {
    return {
      [ContactType.CreditorSupplier]: BuilderPermissionSet.Contacts.CreditorSupplier.ANY,
      [ContactType.Debtor]: BuilderPermissionSet.Contacts.Debtor.ANY,
      [ContactType.Customer]: BuilderPermissionSet.Contacts.Customer.ANY,
      [ContactType.Employee]: BuilderPermissionSet.Contacts.Employee.ANY,
      [ContactType.FinanceCompany]: BuilderPermissionSet.Contacts.FinanceCompany.ANY,
      [ContactType.CityCounty]: BuilderPermissionSet.Contacts.CityCounty.ANY,
      [ContactType.Other]: BuilderPermissionSet.Contacts.Other.ANY,
    }[type];
  }

  private formModeForSalesActivity(): FormMode | boolean {
    if (this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasPermission(BuilderPermission.Sales.Leads.EDIT)) {
      return FormMode.EditMode;
    }
    if (this.userContext.hasPermission(BuilderPermission.Sales.Leads.VIEW)) {
      return  FormMode.ViewMode;
    }
    return false;
  }
}
